import React from 'react';
import { Link } from 'react-router-dom';
import { projects, pillars, Project } from '../data/projectsData';

const ProjectsShowcase: React.FC = () => {
  return (
    <section id="projets" className="projects-showcase">
      <div className="ps-bg-blue" />
      <div className="ps-bg-gold" />

      <div className="ps-inner">
        {/* 4 Pillars */}
        <div className="reveal d1 ps-pillars">
          {pillars.map((p) => (
            <div key={p.title} className="ps-pillar">
              <div className="ps-pillar-icon" style={{ background: `${p.color}14`, color: p.color }}>
                {p.icon}
              </div>
              <div>
                <h3 className="ps-pillar-title">{p.title}</h3>
                <p className="ps-pillar-desc">{p.desc}</p>
              </div>
              <div className="ps-pillar-bullets">
                {p.bullets.map((b) => (
                  <div key={b} className="ps-pillar-bullet">
                    <div className="ps-pillar-dot" style={{ background: p.color }} />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* All 9 projects */}
        <div className="ps-grid">
          {projects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="reveal d3 ps-cta">
          <div>
            <div className="ps-cta-title">Un projet en tête ?</div>
            <div className="ps-cta-sub">Dashboard, IA, intégration ou automatisation — discutons-en.</div>
          </div>
          <Link to="/#contact" className="btn-primary">
            Discuter de votre projet
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </div>

      <style>{showcaseStyles}</style>
    </section>
  );
};

const ProjectCard: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
  const delay = Math.min(index, 4);
  return (
    <article className={`reveal d${delay} ps-card span-${project.span}`}>
      <div className="ps-card-mockup">{project.mockup}</div>
      <div>
        <div className="ps-card-tags">
          {project.tags.map((t) => (
            <span key={t} className="ps-tag" style={{ background: `${project.accent}14`, color: project.accent }}>
              <span className="ps-tag-dot" style={{ background: project.accent }} />
              {t}
            </span>
          ))}
        </div>
        <div className="ps-card-head">
          <div>
            <div className="ps-card-num">— {project.number}</div>
            <h3 className="ps-card-title">{project.title}</h3>
          </div>
          <div className="ps-arrow" style={{ color: project.accent }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10h12M11 4l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <p className="ps-card-desc">{project.description}</p>
        <div className="ps-stack">
          {project.stack.map((s) => (
            <span key={s} className="ps-stack-item">{s}</span>
          ))}
        </div>
      </div>
    </article>
  );
};

/* Styles same as ProjectsTeaser (section has no extra padding top since page header handles it) */
const showcaseStyles = `
  .projects-showcase {
    padding: 3rem 0 6rem;
    background: var(--cream2);
    position: relative;
    overflow: hidden;
  }
  .ps-bg-blue {
    position: absolute; top: -100px; right: -100px;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(26,111,168,.08), transparent 70%);
    pointer-events: none;
  }
  .ps-bg-gold {
    position: absolute; bottom: -100px; left: -100px;
    width: 360px; height: 360px;
    background: radial-gradient(circle, rgba(184,146,42,.08), transparent 70%);
    pointer-events: none;
  }
  .ps-inner { max-width: 1200px; margin: 0 auto; padding: 0 2rem; position: relative; }

  .ps-pillars {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 1px; background: var(--border);
    margin-bottom: 3.5rem; border: 1px solid var(--border);
  }
  .ps-pillar {
    background: var(--white); padding: 1.5rem 1.3rem;
    display: flex; flex-direction: column; gap: .8rem;
  }
  .ps-pillar-icon {
    width: 40px; height: 40px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 4px;
  }
  .ps-pillar-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 1.25rem; font-weight: 600; color: var(--ink);
    margin: 0 0 .4rem;
  }
  .ps-pillar-desc {
    font-family: 'Montserrat', sans-serif;
    font-size: .75rem; font-weight: 300; color: var(--ink2);
    line-height: 1.65; margin: 0;
  }
  .ps-pillar-bullets { display: flex; flex-direction: column; gap: .3rem; margin-top: .3rem; }
  .ps-pillar-bullet {
    display: flex; align-items: center; gap: .5rem;
    font-family: 'Montserrat', sans-serif; font-size: .68rem; color: var(--ink);
  }
  .ps-pillar-dot { width: 4px; height: 4px; border-radius: 50%; flex-shrink: 0; }

  .ps-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 1.5rem; }
  .ps-card {
    background: var(--white); border: 1px solid var(--border);
    padding: 1.5rem; display: flex; flex-direction: column; gap: 1.2rem;
    transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease;
    min-width: 0; overflow: hidden;
  }
  .ps-card > * { min-width: 0; }
  .ps-card.span-large { grid-column: span 12; }
  .ps-card.span-standard { grid-column: span 6; }
  .ps-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 50px -15px rgba(26,111,168,.2);
    border-color: var(--blue);
  }
  .ps-card:hover .ps-arrow { transform: translateX(4px); opacity: 1; }
  .ps-card-mockup { position: relative; width: 100%; min-width: 0; overflow: hidden; border-radius: 8px; }
  .ps-card-mockup > * { max-width: 100%; }

  .ps-cta {
    margin-top: 4rem; padding: 2.5rem 2rem;
    background: var(--white); border: 1px solid var(--border);
    display: flex; justify-content: space-between; align-items: center;
    flex-wrap: wrap; gap: 1.5rem;
  }
  .ps-cta-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 1.6rem; font-weight: 600; color: var(--ink); margin-bottom: .3rem;
  }
  .ps-cta-sub {
    font-family: 'Montserrat', sans-serif;
    font-size: .8rem; color: var(--ink2);
  }

  @media (max-width: 1024px) {
    .ps-pillars { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 768px) {
    .projects-showcase { padding: 2rem 0 4rem; }
    .ps-inner { padding: 0 1rem; }
    .ps-grid { gap: 1rem; grid-template-columns: 1fr; }
    .ps-card.span-large,
    .ps-card.span-standard { grid-column: 1 / -1; }
    .ps-card { padding: .9rem; gap: 1rem; }
    .ps-card-title { font-size: 1.15rem; }
    .ps-card-desc { font-size: .7rem; }
    .ps-cta { padding: 1.5rem 1.1rem; }
    .ps-cta-title { font-size: 1.25rem; }
    .ps-cta-sub { font-size: .72rem; }
  }
  @media (max-width: 600px) {
    .ps-pillars { grid-template-columns: 1fr; }
    .ps-pillar { padding: 1.2rem 1rem; }
  }

  .ps-card-tags { display: flex; gap: .4rem; flex-wrap: wrap; margin-bottom: .7rem; }
  .ps-tag {
    display: inline-flex; align-items: center; gap: .3rem;
    padding: .2rem .55rem;
    font-family: 'Montserrat', sans-serif;
    font-size: .55rem; font-weight: 600;
    letter-spacing: .1em; text-transform: uppercase;
    border-radius: 2px;
  }
  .ps-tag-dot { width: 4px; height: 4px; border-radius: 50%; }
  .ps-card-head {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 1rem; margin-bottom: .5rem;
  }
  .ps-card-num {
    font-family: 'Montserrat', sans-serif;
    font-size: .6rem; font-weight: 600;
    color: var(--ink2); letter-spacing: .2em; margin-bottom: .3rem;
  }
  .ps-card-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 1.4rem; font-weight: 600;
    color: var(--ink); margin: 0; line-height: 1.2;
  }
  .ps-arrow {
    opacity: .5;
    transition: transform .3s ease, opacity .3s ease;
    flex-shrink: 0; margin-top: .3rem;
  }
  .ps-card-desc {
    font-family: 'Montserrat', sans-serif;
    font-size: .78rem; font-weight: 300;
    color: var(--ink2); line-height: 1.7; margin: 0 0 1rem;
  }
  .ps-stack {
    display: flex; gap: .4rem; flex-wrap: wrap;
    padding-top: .9rem; border-top: 1px solid var(--border);
  }
  .ps-stack-item {
    font-family: 'Montserrat', sans-serif;
    font-size: .58rem; font-weight: 500;
    color: var(--ink2); padding: .2rem .5rem;
    border: 1px solid var(--border); border-radius: 2px;
  }

  @keyframes livePulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: .4; transform: scale(1.4); }
  }
  @keyframes caret { 50% { opacity: 0; } }
`;

export default ProjectsShowcase;
